<template>
  <div class="station-form-category-container">
    <div
      v-if="urlConviteDoctor && Number(originalDoctor) === Number(doctor)"
      class="flex flex-col justify-center bg-warning p-3 rounded"
    >
      <span class="font-bold">O médico ainda está pendente!</span>
      <a href="" @click="copyConvite" class="text-sm flex justify-center items-center gap-1">
        <v-icon :icon="mdiContentCopy"></v-icon>
        Clique para copiar o link do convite
      </a>
    </div>

    <div class="flex items-center gap-4 flex-wrap sm:flex-nowrap">
      <v-autocomplete
        @update:model-value="setDoctor"
        :items="listDoctorsComputed"
        :model-value="doctor ? String(doctor) : undefined"
        item-title="title"
        item-value="value"
        :custom-filter="doctorFilter"
        class="flex-1 min-w-[250px]"
        color="primary20"
        clearable
        label="Nome ou CRM do médico"
        theme="light"
        variant="outlined"
        density="comfortable"
        :disabled="!company"
        hide-details="auto"
      >
        <template v-slot:item="{ props, item }">
          <v-list-item
            v-bind="props"
            :title="item?.raw?.title"
            :subtitle="item?.raw?.subtitle"
          ></v-list-item>
        </template>
      </v-autocomplete>

      <filled-button
        :text="isDoctorValidated ? 'Validado' : 'Validar aptidão'"
        height="44px"
        width="180px"
        :loading="props.isLoadingAuthorization"
        @click="props.onValidateDoctor"
        :disabled="disableValidateButton"
      />
    </div>
    <slot name="middle-section" />
    <span class="station-form-category-title" v-if="!props.minimal">Valores</span>
    <div class="station-form-category-content">
      <div class="station-form-category-columns" v-if="!props.minimal">
        <v-text-field
          v-if="!updateForm || modifiedValue || !originalValue"
          @update:model-value="setValue"
          :model-value="value"
          class="w-full max-w-[290px]"
          prefix="R$"
          type="number"
          :label="
            (modifiedValue && modifiedValueEnabled) || !modifiedValueEnabled
              ? 'Valor acordado com o médico'
              : 'Valor original do plantão'
          "
          color="primary20"
          variant="outlined"
          density="comfortable"
        />
        <v-text-field
          v-else
          :model-value="originalValue"
          disabled
          class="w-full max-w-[290px]"
          prefix="R$"
          type="number"
          label="Valor original do plantão"
          color="primary20"
          variant="outlined"
          density="comfortable"
        />
      </div>

      <div class="station-form-category-columns">
        <v-checkbox
          v-if="!props.minimal"
          @click="setInCash"
          :model-value="inCash"
          density="compact"
          label="Pagamento à vista"
          color="primary"
        />
        <v-checkbox
          :disabled="true"
          @click="setAdvanceRequested"
          :model-value="advanceRequested"
          density="compact"
          color="primary"
          label="Antecipação solicitada"
        />
        <v-checkbox
          :disabled="true"
          @click="setAdvancePaid"
          :model-value="advancePaid"
          density="compact"
          color="primary"
          label="Antecipação paga"
        />
        <v-checkbox
          v-if="(updateForm || modifiedValueEnabled) && !props.minimal"
          @click="setModifiedValue"
          :model-value="modifiedValue"
          density="compact"
          color="primary"
          label="Valor Modificado"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { mdiContentCopy } from '@mdi/js'
import { useToast } from 'vue-toastification'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { ref } from 'vue'
const toast = useToast()

const copyConvite = async (e) => {
  e.preventDefault()
  await navigator.clipboard.writeText(props.urlConviteDoctor || '')
  toast.success('Link copiado com sucesso')
}

const props = defineProps([
  'doctor',
  'doctorName',
  'value',
  'company',
  'inCash',
  'modifiedValue',
  'advanceRequested',
  'advancePaid',
  'originalDoctor',
  'urlConviteDoctor',
  'originalValue',
  'updateForm',
  'modifiedValueEnabled',
  'onValidateDoctor',
  'canValidate',
  'isLoadingAuthorization',
  'isDoctorValidatedProp',
  'minimal'
])
const emit = defineEmits([
  'update:doctor',
  'update:doctorName',
  'update:value',
  'update:inCash',
  'update:advanceRequested',
  'update:advancePaid',
  'update:modifiedValue'
])

const isDoctorValidated = ref(true)

const doctorFilter = (itemTitle, queryText, item) => {
  const name = item.raw.title.toLowerCase()
  const crm = item.raw.subtitle.toLowerCase()
  const searchText = queryText.toLowerCase()
  return name.includes(searchText) || crm.includes(searchText)
}

onMounted(() => {
  if (props.doctor) {
    isDoctorValidated.value = true
  }
})

watch(
  () => props.isDoctorValidatedProp,
  (newValue) => {
    isDoctorValidated.value = newValue
  },
  { immediate: true }
)

const disableValidateButton = computed(() => {
  const isSameDoctor = Number(props.doctor) === Number(props.originalDoctor)
  return isDoctorValidated.value || !props.doctor || isSameDoctor
})

const { listDoctorsCompany } = storeToRefs(useDoctorStore())

const listDoctorsComputed = computed(() => {
  //Necessário este computed para adicionar o médico selecionado na lista pois pode ocorrer da listagem não conter o médico selecionado.
  if (listDoctorsCompany.value.filter((item) => item.value === props.doctor).length === 0) {
    return [
      ...listDoctorsCompany.value,
      { value: props.doctor, title: props.doctorName || '', subtitle: '' }
    ]
  }

  return listDoctorsCompany.value
})

const setDoctor = (doctorId: string) => {
  if (doctorId) {
    const selected = listDoctorsCompany.value.find((d) => d.value === doctorId)
    emit('update:doctor', Number(doctorId))
    emit('update:doctorName', selected?.title || '')
    isDoctorValidated.value = false
  } else {
    emit('update:doctor', null)
    emit('update:doctorName', '')
  }
}

const setValue = (value: string) => emit('update:value', Number(value))
const setAdvanceRequested = () => emit('update:advanceRequested', !props.advanceRequested)
const setAdvancePaid = () => emit('update:advancePaid', !props.advancePaid)
const setInCash = () => emit('update:inCash', !props.inCash)
const setModifiedValue = () => emit('update:modifiedValue', !props.modifiedValue)
</script>
